import {
  Box,
  Grid,
  GridItem,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { RouterLink } from '@gamma/ui/navigation';
import { FC, useContext, useRef, useState } from 'react';
import { GPTPrompts } from '../../../..';
import { TruncatedText } from '@investigator/shared/components';
import { ROUTES } from '@investigator/shared/constants';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import formatUnixTimeStampAsDisplayString from '@investigator/shared/utils/formatUnixTimeStampAsDisplayString';
import {
  DetailsGridRow,
  MitreTechniquesGridTruncated,
} from '../../../../../components';
import { safelySetInnerHTML } from '../../../DetectionDetails';
import { LayoutProps } from '../types';
import { stringifyAlertType } from '@investigator/shared/utils';
import { OrgTenantsContext } from '@investigator/shared/providers';

const { detection: detectionText, detectionSummary } = i18n.pages.detections;

const { systemSettingsAlertCatalog } = ROUTES;

const Suricata: FC<LayoutProps> = ({
  detectionInfo,
  metadata,
  detailsProps,
}) => {
  const { start, end } = useDateRangeQueryString();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);

  const {
    alertCategory,
    numberOfAlerts,
    type,
    firstTimeSeen,
    lastTimeSeen,
    mitreTechniques,
    description: descriptionLocale,
    significance,
    validation,
  } = detectionText;

  const {
    alert_info,
    total_alert_count,
    earliest_start_timestamp,
    latest_start_timestamp,
  } = detectionInfo;

  const {
    mitre_mappings: metadata_mitre_mappings,
    description,
    content_doc,
  } = metadata;

  const hasMetaContent = !!(description || content_doc?.significance);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const validationRef = useRef<HTMLDivElement>(null);

  const { seeMore, seeLess } = detectionSummary;

  return (
    <VStack w="full" align="stretch" spacing={4}>
      <HStack align="start">
        <Box flex="1">
          <VStack alignItems="start">
            <HStack w="100%" alignItems="start">
              {alert_info?.alert_name && (
                <Grid templateColumns="1fr" width="100%">
                  <GridItem>
                    <Text color="text.secondary">{alertCategory}</Text>
                  </GridItem>
                  <GridItem
                    data-testid="detection-summary-alert-name-value"
                    overflow="hidden"
                    minW={0}
                  >
                    <TruncatedText>
                      <RouterLink
                        to={`${systemSettingsAlertCatalog(
                          `/${alert_info?.content_id}?start=${start}&end=${end}&tenant=${orgTenantsQueryParam}`,
                        )}`}
                        data-testid="pivot-detection-alert-catalog-link"
                        color="text.link"
                      >
                        {alert_info?.alert_name}
                      </RouterLink>
                    </TruncatedText>
                  </GridItem>
                </Grid>
              )}
              {alert_info?.alert_type && (
                <DetailsGridRow
                  title={type}
                  isDetailsPage
                  data-testid="detection-summary-type"
                >
                  <Text>{stringifyAlertType(alert_info?.alert_type)}</Text>
                </DetailsGridRow>
              )}
              <DetailsGridRow
                title={mitreTechniques}
                isDetailsPage
                data-testid={'metadata_mitre_mappings-label'}
              >
                <MitreTechniquesGridTruncated
                  tactics={metadata_mitre_mappings?.tactics}
                />
              </DetailsGridRow>
              {total_alert_count !== null && (
                <DetailsGridRow
                  isDetailsPage
                  title={numberOfAlerts}
                  data-testid="detection-summary-alert-count"
                >
                  <Text>{total_alert_count}</Text>
                </DetailsGridRow>
              )}
              {earliest_start_timestamp && (
                <DetailsGridRow
                  isDetailsPage
                  title={firstTimeSeen}
                  data-testid="detection-summary-first-timestamp"
                >
                  <Text>
                    {formatUnixTimeStampAsDisplayString(
                      earliest_start_timestamp,
                    )}
                  </Text>
                </DetailsGridRow>
              )}
              {latest_start_timestamp && (
                <DetailsGridRow
                  isDetailsPage
                  title={lastTimeSeen}
                  data-testid="detection-summary-last-timestamp"
                >
                  <Text>
                    {formatUnixTimeStampAsDisplayString(latest_start_timestamp)}
                  </Text>
                </DetailsGridRow>
              )}
            </HStack>
          </VStack>
        </Box>
      </HStack>
      <HStack align="start" maxW="100%">
        <Box w="100%" maxW="100%">
          <VStack alignItems="start">
            <DetailsGridRow
              isDetailsPage
              title={descriptionLocale}
              data-testid="detection-summary-description"
            >
              <Text>{description}</Text>
            </DetailsGridRow>
          </VStack>
        </Box>
        <Box w="100%" maxW="100%">
          <VStack alignItems="start">
            <DetailsGridRow
              isDetailsPage
              title={significance}
              data-testid="detection-summary-description"
            >
              <Text>{content_doc?.significance}</Text>
            </DetailsGridRow>
          </VStack>
        </Box>
      </HStack>
      <HStack align="start" maxW="100%">
        <GPTPrompts
          horizontal
          hideNextSteps
          hideDescription
          detailsProps={detailsProps}
        />
      </HStack>
      <VStack align="start" maxW="100%">
        {isExpanded && hasMetaContent && content_doc?.validation && (
          <DetailsGridRow
            isDetailsPage
            title={validation}
            data-testid="detection-content-doc-validation"
          >
            <Box
              ref={validationRef}
              {...safelySetInnerHTML(content_doc?.validation)}
              __css={{
                ul: {
                  'margin-left': '16px',
                },
              }}
            />
          </DetailsGridRow>
        )}
        {hasMetaContent && content_doc?.validation && (
          <Link color="text.link" onClick={() => setIsExpanded(!isExpanded)}>
            {!isExpanded ? seeMore : seeLess}
          </Link>
        )}
      </VStack>
    </VStack>
  );
};

export default Suricata;
